import { ChangeEvent, useLayoutEffect, useRef, useState } from 'react';
import { Input } from 'components';
import {
  cleanString,
  convertCurrency,
  formatCurrency,
  formatEndEdge,
} from '../../helpers';

interface Props {
  name: string;
  defaultValue: string;
  setFormValue: (newValue: string) => void;
  placeholder: string;
  status?: 'default' | 'success' | 'error';
}

export function CurrencyInput({
  name,
  setFormValue,
  defaultValue,
  placeholder,
  status = 'default',
}: Props) {
  const [value, setRawValue] = useState(
    convertCurrency(formatCurrency(defaultValue))
  );
  const [selection, setSelection] = useState<
    [number | null, number | null] | null
  >(null);
  const ref = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (selection && ref.current) {
      [ref.current.selectionStart, ref.current.selectionEnd] = selection;
    }
  }, [selection]);

  function setValue({ formattedValue }: { formattedValue: string }) {
    setRawValue(formattedValue);
    setFormValue(cleanString(formattedValue));
  }

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const { value: inputVal, selectionStart, selectionEnd } = e.target;

    const cleaned = cleanString(inputVal);

    const allowedStart = 3;
    const allowedEnd = value.length - 2;

    if (value.length === 0) {
      const newPos = allowedStart + 1;

      setSelection([newPos, newPos]);

      const formatted = formatCurrency(cleaned);
      const converted = convertCurrency(formatted);

      return setValue({ formattedValue: converted });
    }

    if (value.length < inputVal.length) {
      if (selectionEnd && selectionEnd > allowedEnd) {
        const formattedValue = formatEndEdge({
          newStr: cleaned,
          allowedEnd,
          currentEnd: selectionEnd,
        });

        const formatted = formatCurrency(formattedValue);
        const converted = convertCurrency(formatted);

        const caretPositionDiff = converted.length - inputVal.length;
        const newPos = allowedEnd + caretPositionDiff;

        setSelection([newPos, newPos]);

        return setValue({ formattedValue: converted });
      }

      if (
        selectionStart === 0 ||
        (selectionStart && selectionStart <= allowedStart)
      ) {
        const formatted = formatCurrency(cleaned);
        const converted = convertCurrency(formatted);

        const newPos = allowedStart + 1;

        setSelection([newPos, newPos]);

        return setValue({ formattedValue: converted });
      }
    }

    const formatted = formatCurrency(cleaned);
    const converted = convertCurrency(formatted);

    const caretPositionDiff = converted.length - inputVal.length;
    const newPos = (selectionEnd || 0) + caretPositionDiff;

    setSelection([newPos, newPos]);

    setValue({ formattedValue: converted });
  }

  return (
    <Input
      status={status}
      name={name}
      forwardRef={ref}
      placeholder={placeholder}
      type="tel"
      value={value}
      onChange={onChange}
    />
  );
}
